import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import {
  Divider,
  LoadingSpinner,
  PageHeaderWrapper,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useGetLastPayout } from "../../Accounts/PayoutSettings/api/useGetLastPayout";
import { Button } from "../../components/Button";
import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { useGetAssignmentByShiftId } from "../../Ten99Policy/api/useGetAssignmentByShiftId";
import { FailedPayoutBannerContainer } from "../FailedPayouts/components/FailedPayoutBannerContainer";
import { useGetWorkerPaymentDetails } from "./api/useGetWorkerPaymentDetails";
import { PaymentDetailsHeader } from "./components/PaymentDetailsHeader";
import { PaymentDetailsPaymentBreakdownSection } from "./components/PaymentDetailsPaymentBreakdownSection";
import { PaymentDetailsPayoutDetailsSection } from "./components/PaymentDetailsPayoutDetailsSection";
import { PAYMENT_DETAILS_FULL_PATH } from "./paths";
import { type PaymentsListItemDetailed } from "./types/PaymentsListItemDetailed";
import { getDisplayableTen99PolicyFeeInCents } from "./utils/getDisplayableTen99PolicyFeeInCents";

interface RouteParams {
  paymentId: string;
}

interface Props {
  modalState: UseModalState;
}

export function PaymentDetailsPage(props: Props) {
  const { modalState } = props;

  const history = useHistory();

  const { paymentId } = useParams<RouteParams>();
  const { data, isLoading } = useGetWorkerPaymentDetails(paymentId);
  const item: PaymentsListItemDetailed | undefined = isDefined(data?.data)
    ? {
        ...data.data,
        shift: data.included.find((entity) => entity.type === "shift"),
        workplace: data.included.find((entity) => entity.type === "workplace"),
        extraTimePay: data.included.find((entity) => entity.type === "extra-time-pay"),
        shiftAdjustment: data.included.find((entity) => entity.type === "shift-adjustment"),
        workedBreak: data.included.find((entity) => entity.type === "worked-break"),
        payout: data.included.find((entity) => entity.type === "payout"),
      }
    : undefined;

  const shiftId = item?.shift?.id;
  const shouldFetchTen99PolicyAssignment =
    isDefined(shiftId) && item?.attributes.paymentCategory === "shift-verification";
  const { data: ten99PolicyAssignment, isLoading: isTen99PolicyAssignmentLoading } =
    useGetAssignmentByShiftId(shiftId ?? "", {
      enabled: shouldFetchTen99PolicyAssignment,
    });
  const ten99PolicyFeeInCents = getDisplayableTen99PolicyFeeInCents({
    item,
    ten99PolicyFeeInCents: ten99PolicyAssignment?.data?.attributes?.assignmentFeeInCents,
  });
  const shouldRenderPaymentBreakdownSection =
    !shouldFetchTen99PolicyAssignment || !isTen99PolicyAssignmentLoading;

  const isPayoutFailed = isDefined(item?.payout) && item.payout.attributes.status === "failed";

  // if payment's payout is failed and it is the last payout -> we wanna show the failed payout banner
  const lastPayout = useGetLastPayout({
    enabled: isPayoutFailed,
  });
  const shouldRenderFailedPayoutBannerContainer =
    isDefined(lastPayout.data) && lastPayout.data.data.id === item?.payout?.id;

  const goToReversedPayment = useCallback(() => {
    if (isDefined(item?.attributes.reversedPaymentId)) {
      history.push(
        PAYMENT_DETAILS_FULL_PATH.replace(":paymentId", item.attributes.reversedPaymentId)
      );
    }
  }, [history, item?.attributes.reversedPaymentId]);

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="tertiary">
      <Box
        sx={(theme) => ({
          height: "100%",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.tertiary,
        })}
      >
        <PageHeaderWrapper variant="tertiary">
          <PageHeaderTopRow onClickBack={modalState.closeModal} />
        </PageHeaderWrapper>
        {isDefined(item) && (
          <Stack spacing={8} sx={(theme) => ({ padding: theme.spacing(5) })}>
            {shouldRenderFailedPayoutBannerContainer && (
              <FailedPayoutBannerContainer sourceUI="payment-details" />
            )}
            <PaymentDetailsHeader item={item} />
            {shouldRenderPaymentBreakdownSection && (
              <PaymentDetailsPaymentBreakdownSection
                item={item}
                payoutFeeInCents={data?.meta.payoutFeeInCents}
                ten99PolicyFeeInCents={ten99PolicyFeeInCents}
              />
            )}
            <PaymentDetailsPayoutDetailsSection
              externalAccount={item.payout?.attributes.destinationDetails}
              isPayoutInstant={item.attributes.payoutMethod === "instant"}
              paymentDate={new Date(item.attributes.createdAt)}
            />
            {isDefined(item?.attributes.reversedPaymentId) && (
              <>
                <Divider />
                <Button fullWidth variant="outlined" onClick={goToReversedPayment}>
                  View reversed payment
                </Button>
              </>
            )}
          </Stack>
        )}
        {!isDefined(item) && isLoading && (
          <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
            <LoadingSpinner size="large" />
          </Box>
        )}
      </Box>
    </FullScreenDialog>
  );
}
